import axios from 'axios'
import { authBridge } from './AuthBridge.js'
import { teardownLocalSession } from '../features/auth/lib/authSession.js'
import { posthogCapture } from './logging/posthog.js'

/**
 * Axios client for React Query
 *
 * Uses AuthBridge.getValidAccessToken() so every request carries a token
 * that was expiry-checked and, when stale, refreshed directly against Kinde.
 */
export const apiClient = axios.create({
  baseURL: `${process.env.DNP_API_BASEURL}/marketplace`,
  timeout: 30000,
})

/**
 * Shared request interceptor — injects a validated Kinde access token.
 * Requests without any session go out unauthenticated; a failed token
 * refresh rejects the request (getValidAccessToken also logs the user out
 * when the refresh token is definitively dead).
 */
async function injectAuthToken(config) {
  const token = await authBridge.getValidAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
}

/**
 * An auth-shaped response error does not prove the session is dead: the
 * backend returns TOKEN_DATA_ERROR for transient Kinde/DB hiccups too, and a
 * 401 can be a request that raced a token rotation. Only a forced refresh
 * against Kinde can tell. Alive → the error was transient, the session (and
 * every other tab) survives; dead → tear down locally. Never Kinde /logout
 * from here — that revokes the SSO session and cascades to all tabs.
 */
async function verifySessionAndTeardown(reason) {
  try {
    const state = await authBridge.verifySession()
    if (state === 'dead') {
      console.warn(`[apiClient] session dead (${reason}) - local teardown`)
      teardownLocalSession(reason)
    } else {
      console.warn(`[apiClient] session ${state} (${reason}) - not logging out`)
    }
  } catch (verifyError) {
    console.error('[apiClient] session verify failed:', verifyError)
  }
}

/**
 * Shared response error handler
 */
function handleResponseError(error) {
  posthogCapture('axios_error', {
    url: error.config?.url,
    method: error.config?.method,
    status: error.response?.status,
    statusText: error.response?.statusText,
    message: error.message,
  })

  if (error.response?.status === 401) {
    console.warn('[apiClient] 401 Unauthorized - verifying session')
    verifySessionAndTeardown('unauthorized')
  }

  // Session possibly ended elsewhere (another tab / the legacy app): this
  // tab's access token still looks valid client-side, but the backend's live
  // Kinde check rejects it with a 500 { code: 'TOKEN_DATA_ERROR' }. The
  // kindeToken guard scopes this to logged-in tabs.
  if (
    error.response?.status === 500 &&
    error.response?.data?.code === 'TOKEN_DATA_ERROR' &&
    localStorage.getItem('kindeToken')
  ) {
    console.warn('[apiClient] 500 TOKEN_DATA_ERROR - verifying session')
    verifySessionAndTeardown('token-data-error')
  }

  if (error.response?.status === 404) {
    console.warn('[apiClient] 404 Not Found:', error.config?.url)
  }

  return Promise.reject(error)
}

apiClient.interceptors.request.use(injectAuthToken, (error) =>
  Promise.reject(error),
)

apiClient.interceptors.response.use((response) => response, handleResponseError)
